import type { LibraryItem } from "@/components/library/library-item"
import type { ShotFrameSource } from "@/lib/workspace-api"

/**
 * Какие типы медиа принимает назначение. Именно список, а не один тип: слот
 * «свои референсы шота» берёт и изображение, и видео (сервер зовёт это media),
 * а опорник — только изображение. Тот же гард есть на сервере (FrameSourceResolver,
 * аргумент expect); здесь он лишь гасит плитку заранее, чтобы человек узнавал
 * о запрете до клика, а не из 422 после.
 *
 * «animatic» и «shot_animatic» — кусок ленты и шотовый ролик: рисуются как видео,
 * но резолвер их не знает. Их принимают только вкладки «Аниматик» и «Превиз»
 * шота (эндпоинты from_source), поэтому обычным видео-слотам они не подходят.
 *
 * «previz» резолвер знает (готовый mp4, иначе исходник), но плитка остаётся
 * служебным типом: её берут только слоты, которые явно перечислили previz
 * (вкладка превиза шота и чат ассистента). Обычный видео-слот её не предлагает,
 * чтобы не спутать привязку записи превиза с подстановкой файла.
 */
export type LibraryPickMedia = "image" | "video" | "audio" | "animatic" | "shot_animatic" | "previz"

/** Причина, по которой плитку нельзя выбрать; UI переводит её ключом library.pick.reason.*, locked — lockedReason. */
export type LibraryUnpickableReason = "wrongMedia" | "notReady" | "noSource" | "locked"

const SERVICE_PICK_MEDIA: readonly LibraryPickMedia[] = ["animatic", "shot_animatic", "previz"]

/**
 * Ключ ассета для набора и для «уже прикреплён». Форма совпадает с canvasReferenceKey
 * (lib/canvas-reference-candidates.ts), поэтому Холст сравнивает свои референсы с
 * выбором в пикере напрямую, без переходника.
 *
 * item.key для этого не годится: он уникален только внутри папки — «storyboard»
 * повторяется в каждой папке шота.
 */
export function librarySourceKey(source: ShotFrameSource): string {
  return `${source.entity_type}:${source.entity_id}${source.state_id != null ? `:${source.state_id}` : ""}`
}

// --- Конструкторы источников: единственное место, где сущность превращается в source ---

export function shotStoryboardSource(shotId: number): ShotFrameSource {
  return { entity_type: "shot_storyboard", entity_id: shotId }
}

export function shotShootingBoardSource(shotId: number): ShotFrameSource {
  return { entity_type: "shot_shooting_board", entity_id: shotId }
}

export function shotVersionFrameSource(versionId: number, state: "first" | "last"): ShotFrameSource {
  return { entity_type: "shot_version_frame", entity_id: versionId, state_id: state }
}

/** Видео версии целиком — источник для видео-референса, в отличие от её первого/последнего кадра. */
export function shotVersionMediaSource(versionId: number): ShotFrameSource {
  return { entity_type: "shot_version_media", entity_id: versionId }
}

export function shotFrameCaptureSource(captureId: number): ShotFrameSource {
  return { entity_type: "shot_frame_capture", entity_id: captureId }
}

export function canvasGenerationSource(generationId: number): ShotFrameSource {
  return { entity_type: "canvas_generation", entity_id: generationId }
}

/** Загрузка ноды флоу: entity_id — id ноды, файл лежит на её вложении media. */
export function flowNodeSource(nodeId: number): ShotFrameSource {
  return { entity_type: "flow_node", entity_id: nodeId }
}

/** «Загрузки проекта»: entity_id — id блоба, принадлежность доказывает сервер через attachments. */
export function projectUploadSource(blobId: number): ShotFrameSource {
  return { entity_type: "project_upload", entity_id: blobId }
}

/**
 * Загрузки Библиотеки: entity_id — id блоба, а не записи ProjectLibraryUpload. Запись
 * удаляема, а назначенный файл после этого живёт дальше — его держит потребитель;
 * указатель по id записи после удаления перестал бы резолвиться.
 */
export function projectLibraryUploadSource(blobId: number): ShotFrameSource {
  return { entity_type: "project_library_upload", entity_id: blobId }
}

/** Эскизы Библиотеки: entity_id — id блоба в Project#sketch_library_files. */
export function projectSketchSource(blobId: number): ShotFrameSource {
  return { entity_type: "project_sketch", entity_id: blobId }
}

/** Звук шота: сервер отдаёт только финальный audio, оригинал записи назначить нельзя. */
export function shotAudioAssetSource(assetId: number): ShotFrameSource {
  return { entity_type: "shot_audio_asset", entity_id: assetId }
}

export function shotReferenceInputSource(inputId: number): ShotFrameSource {
  return { entity_type: "shot_reference_input", entity_id: inputId }
}

/**
 * Сегмент аниматика. В отличие от остальных источников это не файл, а диапазон
 * внутри файла-источника: резолвер его не знает, выбор сам вызывающий пикера
 * отправляет в from_source аниматика или превиза шота.
 */
export function animaticSegmentSource(segmentId: number): ShotFrameSource {
  return { entity_type: "animatic_segment", entity_id: segmentId }
}

/** Превиз: entity_id — запись Previz. Резолвер отдаёт файл; привязка к шоту идёт через from_source. */
export function previzSource(previzId: number): ShotFrameSource {
  return { entity_type: "previz", entity_id: previzId }
}

/**
 * Медиа героев, локаций и реквизита — один тип на все листы, ракурсы и версии.
 * entity_id — id блоба: границу проекта сервер доказывает держателями блоба, а не
 * типом записи, поэтому заводить по типу на каждую версионную таблицу не нужно.
 */
export function projectAssetSource(blobId: number): ShotFrameSource {
  return { entity_type: "project_asset", entity_id: blobId }
}

/**
 * Можно ли выбрать этот элемент под такое назначение. Папки и элементы без source
 * невыбираемы всегда: у legacy-шотов опорник бывает URL-only, без файла на сервере.
 */
export function libraryItemUnpickable(
  item: LibraryItem,
  accepts: readonly LibraryPickMedia[],
): LibraryUnpickableReason | null {
  if (item.kind === "folder") return "noSource"
  // Скриншот в статусе pending/processing файла ещё не имеет — плитка есть, назначать нечего.
  if (item.status != null && item.status !== "ready") return "notReady"
  if (!item.source) return "noSource"
  const media = libraryItemPickMedia(item)
  if (!accepts.includes(media)) return "wrongMedia"
  return item.lockedReason ? "locked" : null
}

/** Что перечислить в «Сюда можно …»: служебные типы при видео — его частные случаи. */
export function libraryNeedMediaKinds(accepts: readonly LibraryPickMedia[]): LibraryPickMedia[] {
  if (!accepts.includes("video")) return [...accepts]
  return accepts.filter((media) => !SERVICE_PICK_MEDIA.includes(media))
}

/** Тип элемента для назначения: обычно выводится из kind, но сегмент аниматика его переопределяет. */
export function libraryItemPickMedia(item: LibraryItem): LibraryPickMedia {
  if (item.pickMedia) return item.pickMedia
  return item.kind === "video" ? "video" : item.kind === "audio" ? "audio" : "image"
}

/** MIME/API media_type → LibraryItem.kind (не схлопывать audio в video). */
export function libraryKindFromMediaType(mediaType: string | null | undefined): "image" | "video" | "audio" {
  if (mediaType === "video") return "video"
  if (mediaType === "audio") return "audio"
  return "image"
}
